import { type ApiSchemas, call, newIdempotencyKey } from '@rental/contracts/client'
import { bodyOf, type Clause, citedClauses, clausesOf } from '@rental/domain'
import { format, formatEuro } from '@rental/i18n'
import { Button, Checkbox, Field, Notice } from '@rental/ui'
import { useBlocker } from '@tanstack/react-router'
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'
import { api } from '../api'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { Section } from '../components/Section'
import { ReasonList } from '../components/SubPanel'
import { TermsApproval, TermsBody } from '../components/TermsText'
import { ProblemNotice, StaffInput, StaffTextarea } from '../components/ui'
import { useSend } from '../fines/send'
import { useLocale } from '../i18n'

type Source = ApiSchemas['StaffTermsVersionText']
type Policy = NonNullable<Source['cancellation']>

interface Row extends Clause {
  /** A key of its own: a clause moved keeps its box, its focus and its undo history. */
  readonly id: number
}

let nextId = 1
const rowsOf = (body: string): Row[] =>
  clausesOf(body).map((clause) => ({ ...clause, id: nextId++ }))

/** The first words of a clause, for the list of the numbers the approval cites. */
function excerpt(text: string): string {
  const line = text.replace(/\s+/g, ' ').trim()
  return line.length > 80 ? `${line.slice(0, 79).trimEnd()}…` : line
}

/**
 * OPS-1, Regole del contratto: the Owner's editor of the rental terms. It
 * starts from a stored version (the one in force, or any opened from the
 * list) and saves a NEW version: a row is never edited (CTR-1). The clauses
 * are the body's paragraphs, numbered in the text itself; the editor shows
 * them as a list, moves and removes them, and writes the numbers back in
 * order. The specific-approval paragraph cites clause numbers in its own
 * words: after a move or a removal the editor lists what each cited number
 * now points at and never rewrites the paragraph. The preview is the
 * customer's rendering. The cancellation policy is configuration (S3), shown
 * read-only. Nothing is translated: everyone signs the Italian terms (Q-26).
 */
export function TermsEditor({
  source,
  onSaved,
}: {
  /** The version the editor starts from; null when none is stored yet. */
  source: Source | null
  onSaved: (version: string) => void
}) {
  const { locale, messages: m } = useLocale()
  const copy = m.terms
  const [title, setTitle] = useState(source?.title ?? '')
  const [rows, setRows] = useState<Row[]>(() => (source ? rowsOf(source.body) : []))
  const [approval, setApproval] = useState(source?.specificApproval ?? '')
  // Q-162: said for these words by whoever writes them; never carried over from the
  // version the editor starts from, so words that drop the clause cannot keep its effect.
  const [secondDriverAllowed, setSecondDriverAllowed] = useState(false)
  const [moved, setMoved] = useState(false)
  const { send, busy, problem } = useSend()
  const reasonsId = useId()
  const discardDialog = useRef<HTMLDialogElement>(null)
  const lastSent = useRef<{ body: string; key: string } | null>(null)

  const body = bodyOf(rows)
  const sameWords =
    source !== null &&
    title.trim() === source.title &&
    body === source.body &&
    approval.trim() === source.specificApproval
  const unchanged = sameWords && secondDriverAllowed === source.secondDriverAllowed
  const empty = title.trim().length === 0 || body.length === 0 || approval.trim().length === 0
  // The box starts unticked whatever the source says: ticking it is a change, leaving it is not.
  const dirty =
    (source ? !sameWords : title !== '' || rows.length > 0 || approval !== '') ||
    secondDriverAllowed
  const dirtyRef = useRef(dirty)
  dirtyRef.current = dirty
  const leaving = useRef(false)

  // The menu, with unsaved words: asked, as the new booking's form asks (OPS-1, D11 widened).
  const blocker = useBlocker({
    shouldBlockFn: () => dirtyRef.current && !leaving.current,
    withResolver: true,
    enableBeforeUnload: false,
  })
  useEffect(() => {
    if (blocker.status === 'blocked') discardDialog.current?.showModal()
  }, [blocker.status])

  const numbered = rows.filter((row) => row.numbered)
  const cited = citedClauses(approval)
  const reasons = [
    ...(empty ? [copy.empty] : []),
    ...(unchanged ? [format(copy.unchanged, { version: source?.version ?? '' })] : []),
  ]
  const canSave = reasons.length === 0 && !busy

  function update(id: number, text: string) {
    setRows((current) => current.map((row) => (row.id === id ? { ...row, text } : row)))
  }
  function move(index: number, by: -1 | 1) {
    setRows((current) => {
      const next = [...current]
      const [row] = next.splice(index, 1)
      if (!row) return current
      next.splice(index + by, 0, row)
      return next
    })
    setMoved(true)
  }
  function remove(index: number) {
    setRows((current) => current.filter((_, at) => at !== index))
    setMoved(true)
  }
  function add() {
    setRows((current) => [...current, { id: nextId++, numbered: true, text: '' }])
  }
  function reset() {
    setTitle(source?.title ?? '')
    setRows(source ? rowsOf(source.body) : [])
    setApproval(source?.specificApproval ?? '')
    setSecondDriverAllowed(false)
    setMoved(false)
  }

  async function save() {
    if (!canSave) return
    const payload = {
      title: title.trim(),
      body,
      specificApproval: approval.trim(),
      ...(secondDriverAllowed ? { secondDriverAllowed: true } : {}),
    }
    // A retry of the same text replays; a changed one gets its own key.
    const serialized = JSON.stringify(payload)
    if (lastSent.current?.body !== serialized) {
      lastSent.current = { body: serialized, key: newIdempotencyKey() }
    }
    const key = lastSent.current.key
    const made = await send(() =>
      call(
        api.POST('/v1/staff/terms/versions', {
          params: { header: { 'idempotency-key': key } },
          body: payload,
        }),
      ),
    )
    if (!made) return
    // Saved: the editor now stands on the new version (the parent reloads it).
    leaving.current = true
    onSaved(made.version)
  }

  const policy: Policy | null = source?.cancellation ?? null
  const feeWords = (fee: { percentBp: number; fixedCents: number }) =>
    format(copy.fee, { percent: fee.percentBp / 100, fixed: formatEuro(fee.fixedCents, locale) })

  return (
    <div className="flex flex-col gap-6">
      <Section title={copy.editor}>
        {source ? (
          <p className="text-sm text-text-muted">
            {format(copy.editing, { version: source.version })}
          </p>
        ) : (
          <p className="text-sm text-text-muted">{copy.editingNone}</p>
        )}
        <p className="text-sm text-text-muted">{copy.language}</p>
        <Field label={copy.versionTitle} required>
          {(control) => (
            <StaffInput
              {...control}
              lang="it"
              maxLength={200}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />
          )}
        </Field>

        <h3 className="text-lg">{copy.clauses}</h3>
        <ol className="flex flex-col gap-4">
          {rows.map((row, index) => {
            const number = row.numbered ? numbered.indexOf(row) + 1 : null
            const name =
              number === null ? copy.clauseUnnumbered : format(copy.clause, { n: number })
            return (
              <li key={row.id} className="flex flex-col gap-2 rounded-sm bg-surface-well p-3">
                <Field label={name}>
                  {(control) => (
                    <StaffTextarea
                      {...control}
                      lang="it"
                      rows={3}
                      value={row.text}
                      onChange={(event) => update(row.id, event.target.value)}
                    />
                  )}
                </Field>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    disabled={index === 0}
                    onClick={() => move(index, -1)}
                    aria-label={`${copy.moveUp}: ${name}`}
                  >
                    <ArrowUp aria-hidden="true" className="size-4" />
                    {copy.moveUp}
                  </Button>
                  <Button
                    variant="outline"
                    disabled={index === rows.length - 1}
                    onClick={() => move(index, 1)}
                    aria-label={`${copy.moveDown}: ${name}`}
                  >
                    <ArrowDown aria-hidden="true" className="size-4" />
                    {copy.moveDown}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => remove(index)}
                    aria-label={`${copy.remove}: ${name}`}
                  >
                    <Trash2 aria-hidden="true" className="size-4" />
                    {copy.remove}
                  </Button>
                </div>
              </li>
            )
          })}
        </ol>
        <div>
          <Button variant="outline" onClick={add}>
            <Plus aria-hidden="true" className="size-4" />
            {copy.add}
          </Button>
        </div>

        <Field label={copy.approval} hint={copy.approvalHint} required>
          {(control) => (
            <StaffTextarea
              {...control}
              lang="it"
              rows={5}
              value={approval}
              onChange={(event) => setApproval(event.target.value)}
            />
          )}
        </Field>
        {moved ? (
          <Notice tone="warning">
            <p>{copy.renumbered}</p>
          </Notice>
        ) : null}
        <div className="flex flex-col gap-1">
          <Checkbox
            label={copy.secondDriverAllowed}
            checked={secondDriverAllowed}
            onChange={(event) => setSecondDriverAllowed(event.target.checked)}
          />
          <p className="text-sm text-text-muted">
            {source?.secondDriverAllowed
              ? format(copy.secondDriverHintFrom, { version: source.version })
              : copy.secondDriverHint}
          </p>
        </div>
        {cited.length > 0 ? (
          <div className="flex flex-col gap-1">
            <h3 className="text-sm font-medium">{copy.cited}</h3>
            <ul className="flex list-disc flex-col gap-1 ps-6 text-sm">
              {cited.map((n) => {
                const clause = numbered[n - 1]
                return (
                  <li key={n}>
                    {clause
                      ? format(copy.citedRow, { n, text: excerpt(clause.text) })
                      : format(copy.citedMissing, { n })}
                  </li>
                )
              })}
            </ul>
          </div>
        ) : null}

        <h3 className="text-lg">{copy.cancellation}</h3>
        <p className="text-sm text-text-muted">{copy.cancellationHint}</p>
        {policy ? (
          <ul className="flex list-disc flex-col gap-1 ps-6 text-sm">
            {policy.tiers.map((tier) => (
              <li key={tier.hoursBefore}>
                {format(copy.tier, { hours: tier.hoursBefore, fee: feeWords(tier) })}
              </li>
            ))}
            <li>{format(copy.noShow, { fee: feeWords(policy.noShow) })}</li>
          </ul>
        ) : (
          <p className="text-sm">{copy.cancellationNone}</p>
        )}

        <ProblemNotice problem={problem} />
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="staff"
            busy={busy}
            disabled={!canSave}
            aria-describedby={reasons.length > 0 ? reasonsId : undefined}
            onClick={() => void save()}
          >
            {copy.save}
          </Button>
          <Button
            variant="outline"
            disabled={!dirty || busy}
            onClick={() => discardDialog.current?.showModal()}
          >
            {copy.discard}
          </Button>
        </div>
        <ReasonList id={reasonsId} reasons={reasons} />
      </Section>

      <Section title={copy.preview}>
        <p className="text-sm text-text-muted">{copy.previewHint}</p>
        <div className="flex flex-col gap-3">
          <h2 lang="it" className="text-xl">
            {title.trim() || copy.untitled}
          </h2>
          <TermsBody body={body} />
          <TermsApproval text={approval.trim()} />
        </div>
      </Section>

      <ConfirmDialog
        dialogRef={discardDialog}
        title={copy.discardTitle}
        confirmLabel={copy.discardConfirm}
        cancelLabel={copy.discardKeep}
        busy={false}
        onConfirm={() => {
          discardDialog.current?.close()
          if (blocker.status === 'blocked') {
            leaving.current = true
            blocker.proceed()
          } else {
            reset()
          }
        }}
        onClose={() => {
          if (blocker.status === 'blocked') blocker.reset()
        }}
      >
        <p>{copy.discardBody}</p>
      </ConfirmDialog>
    </div>
  )
}
